import type {
  InvoicePartySnapshot,
  MockInvoiceLineItem,
} from "./mockInvoiceData";

type InvoicePayerDirtyState = {
  billToParty: InvoicePartySnapshot | null;
  payerSameAsSoldTo: boolean;
  soldToParty: InvoicePartySnapshot;
};

const getInvoicePayerDirtyState = ({
  billToParty,
  payerSameAsSoldTo,
  soldToParty,
}: {
  billToParty: InvoicePartySnapshot;
  payerSameAsSoldTo: boolean;
  soldToParty: InvoicePartySnapshot;
}): InvoicePayerDirtyState => ({
  // A hidden different-payer draft must not keep the form dirty. The backend
  // also omits billToSnapshot when the sold-to party is the payer.
  billToParty: payerSameAsSoldTo ? null : { ...billToParty },
  payerSameAsSoldTo,
  soldToParty: { ...soldToParty },
});

export type CreateInvoiceFormDirtyState = InvoicePayerDirtyState & {
  customerKind: "member" | "non-member";
  dueDate: string;
  issueDate: string;
  lineItems: MockInvoiceLineItem[];
  notes: string;
  referenceNumber: string;
  selectedMemberId: string;
};

export const getCreateInvoiceFormDirtyState = ({
  billToParty,
  customerKind,
  dueDate,
  issueDate,
  lineItems,
  notes,
  payerSameAsSoldTo,
  referenceNumber,
  selectedMemberId,
  soldToParty,
}: {
  billToParty: InvoicePartySnapshot;
  customerKind: "member" | "non-member";
  dueDate: string;
  issueDate: string;
  lineItems: MockInvoiceLineItem[];
  notes: string;
  payerSameAsSoldTo: boolean;
  referenceNumber: string;
  selectedMemberId: string;
  soldToParty: InvoicePartySnapshot;
}): CreateInvoiceFormDirtyState => ({
  ...getInvoicePayerDirtyState({
    billToParty,
    payerSameAsSoldTo,
    soldToParty,
  }),
  customerKind,
  dueDate,
  issueDate,
  // Keep the array order. Invoice line order is part of the saved document.
  lineItems: lineItems.map((lineItem) => ({ ...lineItem })),
  notes,
  referenceNumber,
  selectedMemberId,
});

export type UpdateInvoiceFormDirtyState = InvoicePayerDirtyState & {
  dueDate: string;
  lineItems: MockInvoiceLineItem[];
  notes: string;
  publicMemo: string;
  referencePurchaseOrder: string;
};

export const getUpdateInvoiceFormDirtyState = ({
  billToParty,
  dueDate,
  lineItems,
  notes,
  payerSameAsSoldTo,
  publicMemo,
  referencePurchaseOrder,
  soldToParty,
}: {
  billToParty: InvoicePartySnapshot;
  dueDate: string;
  lineItems: MockInvoiceLineItem[];
  notes: string;
  payerSameAsSoldTo: boolean;
  publicMemo: string;
  referencePurchaseOrder: string;
  soldToParty: InvoicePartySnapshot;
}): UpdateInvoiceFormDirtyState => ({
  ...getInvoicePayerDirtyState({
    billToParty,
    payerSameAsSoldTo,
    soldToParty,
  }),
  dueDate,
  lineItems: lineItems.map((lineItem) => ({ ...lineItem })),
  notes,
  publicMemo,
  referencePurchaseOrder,
});
